import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Star, KeyRound, LifeBuoy, Users, Mail, MailWarning, Trash2, Ban, RotateCcw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { adminGet, adminPost, adminDelete, reportError, formatDate } from './adminApi';

function Stars({ value, size = 'h-4 w-4' }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= Math.round(value || 0) ? 'fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]' : 'text-muted-foreground/40'}`} />)}
    </span>
  );
}

const TICKET_BADGE = { open: 'bg-[hsl(var(--brand-sky))] text-[hsl(var(--brand-ink))]', in_progress: 'bg-secondary text-foreground', resolved: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))]' };

/** Admin: client portal accounts, pending verification codes, satisfaction ratings and tickets. */
export function AdminClientsTab({ adminKey }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/clients/overview', adminKey);
      setData(res.data);
    } catch (err) {
      reportError('espace client', err, (m) => toast.error(m || 'Chargement impossible'));
      setData((d) => d || { accounts: [], pending_codes: [], ratings: [], tickets: [], satisfaction: { global: {}, by_easer: [] } });
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (err) { reportError('espace client', err, (m) => toast.error(m || 'Action impossible')); }
  };

  const q = search.trim().toLowerCase();
  const accounts = useMemo(() => (data?.accounts || []).filter((a) => !q || a.email.includes(q) || (a.name || '').toLowerCase().includes(q)), [data, q]);
  const tickets = useMemo(() => (data?.tickets || []).filter((t) => !q || (t.client_name || '').toLowerCase().includes(q) || t.subject.toLowerCase().includes(q)), [data, q]);
  const ratings = useMemo(() => (data?.ratings || []).filter((r) => !q || (r.client_name || '').toLowerCase().includes(q) || (r.easer_name || '').toLowerCase().includes(q)), [data, q]);

  if (!data) return <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>;

  const g = data.satisfaction?.global || {};
  return (
    <div className="space-y-6" data-testid="admin-clients-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-['Space_Grotesk'] text-xl font-semibold">Espace client</h2>
          <p className="text-sm text-muted-foreground">Comptes, codes de vérification, avis et tickets déposés par vos clients.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un client…" className="h-9 w-56" data-testid="admin-clients-search-input" />
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-clients-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
        </div>
      </div>

      {!data.smtp_configured && (
        <div className="rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 px-4 py-3 text-sm flex items-start gap-2" role="status" data-testid="admin-clients-smtp-warning">
          <MailWarning className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] flex-shrink-0" />
          <span>SMTP non configuré : les codes de vérification ne sont pas envoyés par e-mail. Renseignez-le dans <strong>Paramètres → E-mail (SMTP)</strong> ou communiquez les codes ci-dessous par téléphone.</span>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4" data-testid="admin-clients-kpi-accounts"><p className="text-xs uppercase tracking-wide text-muted-foreground">Comptes clients</p><p className="text-2xl font-semibold mt-1 flex items-center gap-2"><Users className="h-5 w-5 text-muted-foreground" /> {data.accounts.length}</p></Card>
        <Card className="p-4" data-testid="admin-clients-kpi-satisfaction"><p className="text-xs uppercase tracking-wide text-muted-foreground">Satisfaction globale</p>
          {g.count ? <div className="mt-1 flex items-center gap-2"><span className="text-2xl font-semibold">{g.average.toFixed(1).replace('.', ',')}</span><Stars value={g.average} /><span className="text-sm text-muted-foreground">{g.count} avis</span></div> : <p className="text-sm text-muted-foreground mt-2">Aucun avis pour le moment</p>}
        </Card>
        <Card className="p-4" data-testid="admin-clients-kpi-tickets"><p className="text-xs uppercase tracking-wide text-muted-foreground">Tickets ouverts</p><p className="text-2xl font-semibold mt-1 flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-muted-foreground" /> {data.tickets.filter((t) => t.state !== 'resolved').length}</p></Card>
      </div>

      {/* Pending verification codes */}
      <Card className="p-5" data-testid="admin-clients-codes">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-1"><KeyRound className="h-4 w-4" /> Codes de vérification en attente</h3>
        <p className="text-sm text-muted-foreground mb-3">Un client qui crée ou réinitialise son mot de passe reçoit un code par e-mail. S'il ne le reçoit pas, communiquez-le lui ici (valable 30 min).</p>
        {data.pending_codes.length === 0 ? <p className="text-sm text-muted-foreground">Aucun code en attente.</p> : (
          <div className="space-y-2">
            {data.pending_codes.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2" data-testid="admin-client-code-row">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{c.name || c.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.email} · {c.purpose === 'reset' ? 'mot de passe oublié' : 'première connexion'} · expire à {formatDate(c.expires_at, true).split(' ').pop()}</p>
                </div>
                {c.email_sent ? <Badge variant="outline" className="gap-1"><Mail className="h-3 w-3" /> e-mail envoyé</Badge> : <Badge variant="outline" className="gap-1 border-destructive/30 text-destructive" title={c.email_error}><MailWarning className="h-3 w-3" /> e-mail non envoyé</Badge>}
                <span className="font-mono text-lg font-semibold tracking-[0.3em]" data-testid="admin-client-code-value">{c.code}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Accounts */}
      <Card className="p-5" data-testid="admin-clients-accounts">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-3"><Users className="h-4 w-4" /> Comptes clients ({accounts.length})</h3>
        {accounts.length === 0 ? <p className="text-sm text-muted-foreground">Aucun client n'a encore créé son accès.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>E-mail</TableHead><TableHead>Créé le</TableHead><TableHead>Dernière connexion</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {accounts.map((a) => (
                  <TableRow key={a.id} data-testid="admin-client-account-row">
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground">{a.email}</TableCell>
                    <TableCell>{formatDate(a.created_at)}</TableCell>
                    <TableCell>{formatDate(a.last_login, true)}</TableCell>
                    <TableCell>{a.revoked ? <Badge variant="outline" className="border-destructive/30 text-destructive">Désactivé</Badge> : <Badge variant="outline" className="bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))]">Actif</Badge>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {a.revoked
                        ? <Button variant="ghost" size="sm" onClick={() => act(() => adminPost(`/api/admin/clients/accounts/${a.id}/restore`, adminKey), 'Accès rétabli')} data-testid="admin-client-restore"><RotateCcw className="h-4 w-4 mr-1" /> Rétablir</Button>
                        : <Button variant="ghost" size="sm" onClick={() => act(() => adminPost(`/api/admin/clients/accounts/${a.id}/revoke`, adminKey), 'Accès désactivé')} data-testid="admin-client-revoke"><Ban className="h-4 w-4 mr-1" /> Désactiver</Button>}
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => { if (window.confirm(`Supprimer le mot de passe de ${a.name} ? Le client devra refaire une première connexion.`)) act(() => adminDelete(`/api/admin/clients/accounts/${a.id}`, adminKey), 'Compte supprimé'); }} data-testid="admin-client-delete"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Ratings */}
      <Card className="p-5" data-testid="admin-clients-ratings">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-3"><Star className="h-4 w-4" /> Avis clients ({ratings.length})</h3>
        {(data.satisfaction?.by_easer || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-clients-by-easer">
            {data.satisfaction.by_easer.map((e) => (
              <div key={e.easer_id} className="rounded-lg border border-border px-3 py-1.5 text-sm flex items-center gap-2"><span className="notranslate font-medium">{e.easer_name || `Easer #${e.easer_id}`}</span><Stars value={e.average} size="h-3.5 w-3.5" /><span className="text-muted-foreground">{e.average.toFixed(1).replace('.', ',')} · {e.count}</span></div>
            ))}
          </div>
        )}
        {ratings.length === 0 ? <p className="text-sm text-muted-foreground">Aucun avis pour le moment.</p> : (
          <div className="space-y-2">
            {ratings.map((r) => (
              <div key={r.id} className="flex flex-wrap items-start gap-3 rounded-lg border border-border px-3 py-2" data-testid="admin-client-rating-row">
                <Stars value={r.stars} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm"><span className="font-medium">{r.client_name}</span> <span className="text-muted-foreground">→ Easer <span className="notranslate">{r.easer_name || `#${r.easer_id}`}</span> · {r.label} · {formatDate(r.event_date)}</span></p>
                  {r.comment && <p className="text-sm mt-1 italic">« {r.comment} »</p>}
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" aria-label="Supprimer l'avis" onClick={() => { if (window.confirm('Supprimer cet avis ?')) act(() => adminDelete(`/api/admin/clients/ratings/${r.id}`, adminKey), 'Avis supprimé'); }} data-testid="admin-client-rating-delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tickets */}
      <Card className="p-5" data-testid="admin-clients-tickets">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-1"><LifeBuoy className="h-4 w-4" /> Tickets clients ({tickets.length})</h3>
        <p className="text-sm text-muted-foreground mb-3">Chaque ticket est créé dans le module Tickets de Dolibarr : traitez-le là-bas, le statut se met à jour ici automatiquement.</p>
        {tickets.length === 0 ? <p className="text-sm text-muted-foreground">Aucun ticket.</p> : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.id} className="rounded-lg border border-border px-3 py-2" data-testid="admin-client-ticket-row">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={TICKET_BADGE[t.state] || ''}>{t.status_label}</Badge>
                  <span className="font-medium">{t.subject}</span>
                  <span className="text-xs text-muted-foreground">{t.category_label} · {t.ref} · {t.client_name} · {formatDate(t.created_at, true)}</span>
                  {t.dolibarr_id && <a className="ml-auto text-xs inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline" href={`${(data.dolibarr_url || '').replace(/\/$/, '')}/ticket/card.php?id=${t.dolibarr_id}`} target="_blank" rel="noopener noreferrer" data-testid="admin-client-ticket-dolibarr-link">Ouvrir dans Dolibarr <ExternalLink className="h-3 w-3" /></a>}
                </div>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line line-clamp-3">{t.message}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
